import { SurveyRoutingStep } from "types/surveyTest.types"

/**
 * One piece of a routing condition as the side panel shows it: a question named on the left of a test, an
 * option the test names, an operator or lead in words, plain text (numbers, and anything not recognised),
 * a parenthesis, or the AND / OR that starts a new line.
 */
export interface Segment {
  kind: "question" | "option" | "word" | "text" | "paren" | "connector"
  text: string
}

/**
 * A piece of a premise as written: a `<<name>>`, a `"quoted"` constant (its text without the quotes), a
 * number, a word, an operator or a punctuation mark.
 */
interface Token {
  t: "name" | "quoted" | "num" | "word" | "op" | "punct"
  text: string
}

const seg = (kind: Segment["kind"], text: string): Segment => ({ kind, text })

/** Comparison operators, the longest first so "<=" is not read as "<" then "=", nor "!=" as "!" then "=". */
const OPS = ["<=", ">=", "<>", "!=", "<", ">", "="]

/** Comparisons in words; "!=" is another spelling of "<>". */
const OP_WORDS = new Map([
  ["=", "is"],
  ["<>", "is not"],
  ["!=", "is not"],
  ["<", "is less than"],
  ["<=", "is at most"],
  [">", "is more than"],
  [">=", "is at least"],
])

/** Set tests in words: ANY OF, NONE OF and ALL OF. IN and NOT IN, other spellings of the first two, are read apart. */
const SET_WORDS = new Map([
  ["ANY", "is any of"],
  ["NONE", "is none of"],
  ["ALL", "has all of"],
])

/** A character of a bare word or a number: not a space, a mark, an operator, nor the "!" of "!=". */
const WORD_CHAR = String.raw`(?:(?!!=)[^\s<>=(){},])`
const NUM = new RegExp(String.raw`^-?\d+(?:\.\d+)?(?!${WORD_CHAR})`)
const WORD = new RegExp(`^${WORD_CHAR}+`)

/** Splits a premise into tokens. Every character but white space lands in a token, so nothing is dropped. */
const tokenize = (premise: string): Token[] => {
  const tokens: Token[] = []
  let i = 0
  while (i < premise.length) {
    const rest = premise.slice(i)
    const space = /^\s+/.exec(rest)
    if (space) {
      i += space[0].length
      continue
    }
    if (rest.startsWith("<<")) {
      const end = rest.indexOf(">>", 2)
      // An unclosed name is kept as written.
      if (end < 0) {
        tokens.push({ t: "word", text: rest })
        break
      }
      tokens.push({ t: "name", text: rest.slice(2, end).trim() })
      i += end + 2
      continue
    }
    if (rest.startsWith('"')) {
      const end = rest.indexOf('"', 1)
      // An unclosed quote is kept as written.
      if (end < 0) {
        tokens.push({ t: "word", text: rest })
        break
      }
      tokens.push({ t: "quoted", text: rest.slice(1, end) })
      i += end + 1
      continue
    }
    const op = OPS.find((o) => rest.startsWith(o))
    if (op) {
      tokens.push({ t: "op", text: op })
      i += op.length
      continue
    }
    if ("(){},".includes(rest[0])) {
      tokens.push({ t: "punct", text: rest[0] })
      i += 1
      continue
    }
    const num = NUM.exec(rest)
    if (num) {
      tokens.push({ t: "num", text: num[0] })
      i += num[0].length
      continue
    }
    const word = WORD.exec(rest) as RegExpExecArray
    tokens.push({ t: "word", text: word[0] })
    i += word[0].length
  }
  return tokens
}

const upper = (tok: Token | undefined): string => (tok?.t === "word" ? tok.text.toUpperCase() : "")

/** The members of a `{ }` set, from the token after the brace: each is an option, as written between the commas. */
const readSet = (tokens: Token[], from: number): { options: Segment[]; next: number } => {
  const options: Segment[] = []
  let item: string[] = []
  const flush = () => {
    if (item.length) options.push(seg("option", item.join(" ")))
    item = []
  }
  let i = from
  for (; i < tokens.length && !(tokens[i].t === "punct" && tokens[i].text === "}"); i++) {
    if (tokens[i].t === "punct" && tokens[i].text === ",") flush()
    else item.push(tokens[i].text)
  }
  flush()
  return { options, next: i + 1 }
}

/**
 * A premise as written in the questionnaire, as display segments: `<<name>>` is a question on the left of
 * a test and an option after the operator or inside `{ }`; a bare word after the operator or inside `{ }`
 * is an option too, and so is a `"quoted"` constant; a number is plain. Operators are put in words, AND
 * and OR start a new line, and the parentheses stay, but for COUNT's. Keywords are read in any case;
 * anything else shows as written.
 */
export const parsePremise = (premise: string): Segment[] => {
  const tokens = tokenize(premise)
  const out: Segment[] = []
  // After an operator: the next name or bare word is the option the test names.
  let operand = false
  // One entry per open parenthesis: COUNT's is not shown.
  const parens: boolean[] = []

  /** An opening parenthesis at `i`. */
  const opens = (i: number): boolean => tokens[i]?.t === "punct" && tokens[i].text === "("
  /** A keyword at `i` that ends a run of bare words on the left of a test. */
  const keywordAt = (i: number): boolean => {
    const w = upper(tokens[i])
    if (["IS", "NOT", "IN", "AND", "OR", "WHERE"].includes(w)) return true
    if (SET_WORDS.has(w)) return upper(tokens[i + 1]) === "OF"
    return w === "COUNT" && opens(i + 1)
  }
  /** A test, or a grid's WHERE, starts at `i`: an operator, IS, a set test (IN and NOT IN included) or WHERE. */
  const testAt = (i: number): boolean => {
    const w = upper(tokens[i])
    const next = upper(tokens[i + 1])
    return (
      tokens[i]?.t === "op" ||
      ["IS", "IN", "WHERE"].includes(w) ||
      (w === "NOT" && next === "IN") ||
      (SET_WORDS.has(w) && next === "OF")
    )
  }

  for (let i = 0; i < tokens.length; i++) {
    const tok = tokens[i]
    const w = upper(tok)
    if (tok.t === "name") {
      out.push(seg(operand ? "option" : "question", tok.text))
      operand = false
    } else if (tok.t === "quoted") {
      out.push(seg("option", tok.text))
      operand = false
    } else if (tok.t === "num") {
      out.push(seg("text", tok.text))
      operand = false
    } else if (tok.t === "op") {
      out.push(seg("word", OP_WORDS.get(tok.text) ?? tok.text))
      operand = true
    } else if (tok.t === "punct") {
      if (tok.text === "{") {
        const { options, next } = readSet(tokens, i + 1)
        out.push(...options)
        i = next - 1
        operand = false
      } else if (tok.text === "(") {
        parens.push(false)
        out.push(seg("paren", "("))
      } else if (tok.text === ")") {
        if (!parens.pop()) out.push(seg("paren", ")"))
        operand = false
      } else out.push(seg("text", tok.text))
    } else if (w === "IS") {
      const not = upper(tokens[i + 1]) === "NOT"
      out.push(seg("word", not ? "is not" : "is"))
      if (not) i++
      operand = true
    } else if (w === "NOT" && upper(tokens[i + 1]) === "IN") {
      out.push(seg("word", "is none of"))
      i++
      operand = true
    } else if (w === "NOT") {
      out.push(seg("word", "not"))
    } else if (w === "IN" && !operand) {
      // An option named "In" after the operator stays an option.
      out.push(seg("word", "is any of"))
      operand = true
    } else if (w === "AND" || w === "OR") {
      out.push(seg("connector", w.toLowerCase()))
      operand = false
    } else if (SET_WORDS.has(w) && upper(tokens[i + 1]) === "OF") {
      out.push(seg("word", SET_WORDS.get(w) as string))
      i++
      operand = true
    } else if (w === "COUNT" && opens(i + 1)) {
      out.push(seg("word", "number ticked in"))
      parens.push(true)
      i++
      operand = false
    } else if (w === "WHERE") {
      const select = upper(tokens[i + 1]) === "SELECT"
      out.push(seg("word", select ? "for column" : "for"))
      if (select) i++
      operand = false
    } else if (operand) {
      // An option written bare, as in IS Panel: its words up to AND or OR, or the next mark.
      const words = [tok.text]
      while (tokens[i + 1]?.t === "word" && !["AND", "OR"].includes(upper(tokens[i + 1]))) words.push(tokens[++i].text)
      out.push(seg("option", words.join(" ")))
      operand = false
    } else {
      // Bare words on the left of a test name its question; elsewhere they show as written.
      const words = [tok.text]
      while (tokens[i + 1]?.t === "word" && !keywordAt(i + 1)) words.push(tokens[++i].text)
      out.push(seg(testAt(i + 1) ? "question" : "text", words.join(" ")))
    }
  }
  return out
}

/** A name with its `<<` `>>` taken off, as SKIP TO and FOR EACH may write it. */
const bare = (name: string): string => {
  const t = name.trim()
  return t.startsWith("<<") && t.endsWith(">>") ? t.slice(2, -2).trim() : t
}

/**
 * A routing step as one readable condition: "If", "Terminate if" or "Skip to <target> if" and the premise,
 * or "Loop over <driver>: <iteration>". A kind it does not know shows as written, then its text.
 */
export const stepSegments = (step: SurveyRoutingStep): Segment[] => {
  const kind = step.kind.trim().toUpperCase()
  const asWritten = [seg("word", step.kind), seg("text", step.text)]
  if (kind === "IF") return [seg("word", "If"), ...parsePremise(step.text)]
  if (kind === "TERMINATE") return [seg("word", "Terminate if"), ...parsePremise(step.text)]
  if (kind === "SKIP TO") {
    // "premise -> target": the premise may hold "->" itself, the target never does.
    const at = step.text.lastIndexOf(" -> ")
    if (at < 0) return asWritten
    return [
      seg("word", "Skip to"),
      seg("question", bare(step.text.slice(at + 4))),
      seg("word", "if"),
      ...parsePremise(step.text.slice(0, at)),
    ]
  }
  if (kind === "FOR EACH") {
    // "driver = iteration"
    const at = step.text.indexOf(" = ")
    if (at < 0) return [seg("word", "Loop over"), seg("question", bare(step.text))]
    return [
      seg("word", "Loop over"),
      seg("question", bare(step.text.slice(0, at))),
      seg("text", ":"),
      seg("option", bare(step.text.slice(at + 3))),
    ]
  }
  return asWritten
}

/** The segments as lines: each AND or OR starts a new one. */
export const premiseLines = (segments: Segment[]): Segment[][] => {
  const lines: Segment[][] = [[]]
  for (const s of segments) {
    if (s.kind === "connector" && lines[lines.length - 1].length) lines.push([])
    lines[lines.length - 1].push(s)
  }
  return lines.filter((l) => l.length)
}
